import { MouseEvent, useState } from 'react';
import S from './DetailsPanelStyles';
import { Driver } from '../../API/types/Drivers';
import { Vehicle } from '../../API/types/Vehicles';
import { Device } from '../../API/types/Devices';
import ExpandedItem from '../common/ExpandedItem/ExpandedItem';
import useDriverThumbnail from '../../theme/useDriverThumbnail';
import useVehicleThumbnail from '../../theme/useVehicleThumbnail';
import DeviceThumbnail from '../../assets/images/device-thumbnail-expanded.png';
import ExpandedItemsWrapper from '../ui/ExpandedItemsWrapper/ExpandedItemsWrapper';
import { generateDistance } from '../../utils/unitsUtils';
import { getFormattedDate, getDeviceLastActivityLabel } from '../../utils/dateUtils';
import Modal from '../common/Modal/Modal';
import AssignDriverToTrip from '../../pages/TripDetails/AssignDriverToTrip/AssignDriverToTrip';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import { FeaturesManagement } from '../../featuresControl';
import Icon from '../common/Icon/Icon';
import useUnassignDriverFromTrip from '../../API/hooks/trips/useUnassignDriverFromTrip';
import DeleteModal from '../common/DeleteModal/DeleteModal';
import useGetRole from '../../API/hooks/useGetRole';
import { IconKeys } from '../common/Icon/IconKeys';
import useGetDeviceConfiguration from '../../API/hooks/devices/useGetDeviceConfiguration';
import { getDeviceConfigStatusDisplay } from '../../utils/deviceConfigStatusDisplay';
import useGetAlertsAggregation from '../../API/hooks/alerts/useGetAlertsAggregation';
import { getSimpleEqQuery } from '../../utils/queryFilters';
import { generateEventsLabel } from '../../utils/commonUtils';

type Props = {
  driver?: Driver | null,
  vehicle?: Vehicle | null,
  device?: Device | null,
  entity?: 'vehicle' | 'driver' | 'device' | 'alert' | 'trip',
  entityId?: string,
  allowTripDriverAssignment?: boolean,
};

function DetailsPanel({
  driver, vehicle, device, entity, entityId, allowTripDriverAssignment = true,
}: Props): JSX.Element {
  const fleetId = useGetFleetId();
  const vehicleThumbnail = useVehicleThumbnail();
  const driverThumbnail = useDriverThumbnail();
  const { isAdmin, isSupport, isFleetManager } = useGetRole();
  const { data: deviceConfig, isLoading: isCfgLoading, isError: isCfgError } = useGetDeviceConfiguration(
    device?.deviceId ?? '',
    fleetId ?? '',
  );
  const cfg = device
    ? getDeviceConfigStatusDisplay(deviceConfig?.status, isCfgLoading, isCfgError)
    : null;
  const devicePanelConfigDisplay = cfg
    ? {
      text: cfg.text,
      icon: cfg.icon,
      color: cfg.color,
      textColor: cfg.textColor,
    }
    : undefined;
  const { data: driverEvents } = useGetAlertsAggregation(
    fleetId,
    `?limit=1000&skip=0${getSimpleEqQuery('driverId', driver?._id, 0)}`,
    !!driver?._id,
  );
  const { data: vehicleEvents } = useGetAlertsAggregation(
    fleetId,
    `?limit=1000&skip=0${getSimpleEqQuery('vehicleId', vehicle?._id, 0)}`,
    !!vehicle?._id,
  );
  const [assignDriverModalState, toggleAssignDriverModalState] = useState(false);
  const [unassignDriverModalState, toggleUnassignDriverModalState] = useState(false);
  const { mutate: unassignDriverToTrip } = useUnassignDriverFromTrip(() => {}, () => {});
  const canAssignDriverToTrip = entity === 'trip'
    && FeaturesManagement.features.tripDriverAssignment
    && allowTripDriverAssignment;

  const assignDriverToTrip = (): void => {
    if (!canAssignDriverToTrip) {
      return;
    }
    toggleAssignDriverModalState(true);
  };
  const unassignDriverFromTrip = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    if (!canAssignDriverToTrip) {
      return;
    }
    toggleUnassignDriverModalState(true);
  };

  const onUnassignDriver = (): void => {
    if (!canAssignDriverToTrip || !entityId) {
      toggleUnassignDriverModalState(false);
      return;
    }
    unassignDriverToTrip({ fleetId, tripId: entityId });
    toggleUnassignDriverModalState(false);
  };

  const unassignDriver = (
    <button
      className="unassign-btn"
      title="Unassign driver"
      type="button"
      onClick={unassignDriverFromTrip}
    >
      <Icon iconKey={IconKeys.DELETE_USER_DATA} />
    </button>
  );
  const noDriverButton = (
    <button type="button" className="action-button" onClick={assignDriverToTrip}>Assign driver</button>
  );
  return (
    <S.Wrapper>
      <ExpandedItemsWrapper>
        {!!vehicle && (
          <ExpandedItem
            type="vehicle"
            item={{
              image: vehicleThumbnail,
              name: vehicle.licensePlateNumber,
              title: `${vehicle.manufacturer ? (`${vehicle.manufacturer} `) : ''}${vehicle.model ? (`${vehicle.model} `) : ''}`,
              score: vehicle.score !== undefined ? vehicle.score : 'N/A',
              category: vehicle.category,
              status: vehicle.status,
              mileage: vehicle.distanceInKmTotal && generateDistance(vehicle.distanceInKmTotal, true),
              eventsLabel: vehicleEvents?.count != null ? generateEventsLabel(vehicleEvents.count) : undefined,
            }}
            link={vehicle?._id ? `/vehicles/${vehicle._id}` : undefined}
          />
        )}
        {!vehicle && (
          <ExpandedItem
            type="vehicle"
            item={{
              image: vehicleThumbnail,
              name: 'No assigned vehicle',
              title: '',
              mileage: 'N/A',
            }}
          />
        )}
        {!!driver && (
          <ExpandedItem
            type="driver"
            item={{
              image: (driver as { image?: string }).image || driver?.avatarUrl || driverThumbnail,
              name: (driver as { name?: string }).name || `${driver.firstName} ${driver.lastName}`,
              title: driver.licenseName,
              vehicleClassType: driver.vehicleClassType,
              score: driver.score >= 0 ? driver.score : undefined,
              mileage: driver.distanceInKmTotal && generateDistance(driver.distanceInKmTotal, true),
              licenseCheck: driver.licenseExpireAt ? `License exp. ${getFormattedDate(driver.licenseExpireAt)}` : 'No data about license exp.',
              eventsLabel: driverEvents?.count != null ? generateEventsLabel(driverEvents.count) : undefined,
            }}
            link={driver?._id ? `/drivers/${driver._id}` : undefined}
            obj={canAssignDriverToTrip ? unassignDriver : undefined}
          />
        )}
        {!driver && (
          <ExpandedItem
            type="driver"
            item={{
              image: driverThumbnail,
              name: 'No assigned driver',
              title: '',
              license: 'N/A',
            }}
            obj={canAssignDriverToTrip ? noDriverButton : undefined}
          />
        )}
        {!!device && (
          <ExpandedItem
            type="device"
            item={{
              image: DeviceThumbnail,
              hwModel: `HW: ${device.hwModel}`,
              fwVersion: isAdmin || isSupport || isFleetManager ? `FW: ${device.fwVersion}` : undefined,
              status: device.isOnline ? 'Online' : 'Offline',
              deviceId: device.deviceId,
              configDisplay: devicePanelConfigDisplay,
              lastActivityLabel: getDeviceLastActivityLabel(device.activeAt, device.isOnline),
            }}
            link={device?._id ? `/devices/${device._id}` : undefined}
          />
        )}
        {!device && (
          <ExpandedItem
            type="device"
            item={{
              image: DeviceThumbnail,
              deviceId: 'No assigned device',
            }}
          />
        )}
      </ExpandedItemsWrapper>
      {canAssignDriverToTrip && assignDriverModalState && (
        <Modal
          title="Assign driver to trip"
          state={assignDriverModalState}
          toggleState={toggleAssignDriverModalState}
        >
          <AssignDriverToTrip
            onCancel={() => toggleAssignDriverModalState(false)}
            fleetId={fleetId}
            tripId={entityId || ''}
          />
        </Modal>
      )}
      {canAssignDriverToTrip && unassignDriverModalState && (
        <Modal
          title="Unassign driver from trip"
          state={unassignDriverModalState}
          toggleState={toggleUnassignDriverModalState}
        >
          <DeleteModal
            title="Are you sure you want to unassign this driver from trip?"
            onClose={() => toggleUnassignDriverModalState(false)}
            onDelete={onUnassignDriver}
            btnConfirmText="Unassign"
          />
        </Modal>
      )}
    </S.Wrapper>
  );
}

export default DetailsPanel;
